import { useCallback, useMemo } from "react";
import type { OnChangeFn, SortingState } from "@salestrak/ui";

import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import type { StageOption } from "#app/lib/data";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { ISSUE_LIST } from "#app/lib/filters/list-configs";
import { useTableFilters, type FilterDef } from "#app/lib/hooks/use-table-filters";
import { useIssueFilters } from "#app/lib/filters/issue-filter-context";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import type { useUserPool, useTeamPool } from "#app/components/entity-selectors";
import { scopeWhere, type IssueScope } from "#app/lib/filters/issue-scope";
import {
  AnalyticsContext,
  AnalyticsView,
  IssueEvent,
  useAnalytics,
  useTrackSearch,
} from "#app/lib/analytics";
import { applyCurrentUserFilter, combineWhereConditions } from "#app/lib/query-utils";
import type { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { viewArchivedWhere, viewStageWhere, type ViewConfig } from "#app/lib/view-config";

/**
 * The issue list's filter layers and the query they fold into: the toolbar's
 * table filters, the provider's two-layer `where`, the view's own predicate
 * (scope, archived, kept stages), the Only-Me overlay, the view-owned sort
 * and the list query state that carries search, sorting and the ungrouped
 * `where`/`orderBy`.
 */
export function useIssuesTableFilters({
  scope,
  availableStages,
  userPool,
  teamPool,
  isCurrentUserFilterApplied,
  isGrouped,
  groupByField,
  config,
  setConfig,
  workspaceStages,
}: {
  scope: IssueScope;
  availableStages: StageOption[];
  userPool: ReturnType<typeof useUserPool>["pool"];
  teamPool: ReturnType<typeof useTeamPool>["pool"];
  isCurrentUserFilterApplied: boolean;
  isGrouped: boolean;
  groupByField: string;
  config: ViewConfig;
  setConfig: (patch: Partial<ViewConfig>) => void;
  workspaceStages: ReturnType<typeof useWorkspaceData>["stages"];
}) {
  const filterDefs = useMemo<FilterDef[]>(
    () => [
      {
        key: "stageId",
        label: "Stage",
        icon: "layers",
        type: "select",
        operator: "in",
        options: availableStages.map((s) => ({
          value: s.stageId,
          label: s.name,
        })),
      },
      {
        key: "assignees",
        label: "Assignee",
        icon: ASSIGNEE_ICON,
        type: "entity",
        filterFormat: "entityRef",
        operator: "hasSome",
        pools: [userPool],
        placeholder: "Search members...",
      },
      // The project's own Teams property.
      {
        key: "teamIds",
        label: "Teams",
        icon: PROPERTY_ICON.team,
        type: "entity",
        operator: "hasSome",
        pools: [teamPool],
        placeholder: "Search teams...",
      },
    ],
    [availableStages, userPool, teamPool],
  );

  const tableFilters = useTableFilters(filterDefs);
  const {
    filters: panelFilters,
    where: screenFilterWhere,
    activeFilterCount: screenFilterCount,
    viewFilterCount,
    viewNoteCount,
    quickLayerFor,
    clearAll: clearAllFilters,
    config: filterConfig,
    filterPanelOpen,
    setFilterPanelOpen,
  } = useIssueFilters();

  // Combine the screen's filters (the view's own layer ANDed with the
  // toolbar's temporary pool — one `where` off the provider) and the
  // table-level filters with the "Only Me" toggle (current-user only when
  // ungrouped — grouped queries fold it per group via getGroupWhere instead).
  const filterWhere = useMemo(() => {
    const conditions: Record<string, unknown>[] = [];
    if (screenFilterWhere) conditions.push(screenFilterWhere);
    if (tableFilters.where) conditions.push(tableFilters.where);
    const combined = combineWhereConditions(conditions);
    return applyCurrentUserFilter(
      combined,
      isCurrentUserFilterApplied && !isGrouped,
    );
  }, [
    screenFilterWhere,
    tableFilters.where,
    isCurrentUserFilterApplied,
    isGrouped,
  ]);

  // Scope + archived + stage scoping are the view's own predicate, not the
  // user's — they lead the AND fold ahead of search and filters.
  // `groupBaseWhere` below repeats them because grouped queries are built
  // from that base instead. "Show archived" widens the list rather than
  // narrowing it: off, the archived rows are excluded; on, nothing filters
  // on `archived` at all. The completed / canceled toggles narrow the same
  // way, by naming the stages the view keeps.
  const stageWhere = useMemo(
    () => viewStageWhere(config, workspaceStages),
    [config, workspaceStages],
  );
  const issueBaseWhere = useMemo(() => {
    const conditions: Record<string, unknown>[] = [scopeWhere(scope)];
    const archivedWhere = viewArchivedWhere({ showArchived: config.showArchived }, screenFilterWhere, tableFilters.where);
    if (archivedWhere) conditions.push(archivedWhere);
    if (stageWhere) conditions.push(stageWhere);
    return conditions;
  }, [scope, config.showArchived, screenFilterWhere, tableFilters.where, stageWhere]);

  // The sort is view config, not table state: the panel's Sort by row and the
  // column headers write the same draft key, so either one previews
  // immediately and settles on Save. `none` sends no sorting and the server
  // falls back to the list's default order (creationDate desc).
  const viewSorting = useMemo<SortingState>(
    () =>
      config.sortBy === "none"
        ? []
        : [{ id: config.sortBy, desc: config.sortDirection === "desc" }],
    [config.sortBy, config.sortDirection],
  );
  const { track } = useAnalytics();
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const next = typeof updater === "function" ? updater(viewSorting) : updater;
      const primary = next[0];
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.ChangeSorting,
        data: {
          view: AnalyticsView.List,
          scope: scope.kind,
          field: primary?.id ?? "none",
          via: "column_header",
        },
      });
      if (!primary) {
        setConfig({ sortBy: "none" });
      } else {
        setConfig({
          sortBy: primary.id,
          sortDirection: primary.desc ? "desc" : "asc",
        });
      }
    },
    [viewSorting, setConfig, track, scope.kind],
  );

  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    searchWhere,
    orderBy,
    where,
  } = useListQueryState(ISSUE_LIST, {
    baseWhere: issueBaseWhere,
    filterWhere,
    sorting: viewSorting,
    onSortingChange: handleSortingChange,
  });

  // Unsorted stage groups keep the hand order the board keeps — each group
  // reads its rows by `sortOrder`, so dragging a row to a new slot lands it
  // there on both surfaces. Any other grouping, or an active Sort by, keeps
  // the list's own order.
  const handOrder = groupByField === "stage" && viewSorting.length === 0;
  const groupOrderBy = useMemo<Record<string, string> | undefined>(
    () => (handOrder ? { sortOrder: "asc" } : orderBy),
    [handOrder, orderBy],
  );
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Issue,
    event: IssueEvent.Search,
    data: { view: AnalyticsView.List, scope: scope.kind },
  }));

  return {
    tableFilters,
    panelFilters,
    screenFilterWhere,
    screenFilterCount,
    viewFilterCount,
    viewNoteCount,
    quickLayerFor,
    clearAllFilters,
    filterConfig,
    filterPanelOpen,
    setFilterPanelOpen,
    stageWhere,
    viewSorting,
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    searchWhere,
    orderBy,
    where,
    handOrder,
    groupOrderBy,
  };
}
